import confetti from "canvas-confetti";

const RAINBOW_COLORS = [
  "#FF63AC",
  "#FFA844",
  "#FFFF45",
  "#68FF88",
  "#5DB9FF",
  "#CD68FF",
  "#5452CD",
];

const CONFETTI_DEFAULTS = {
  colors: RAINBOW_COLORS,
  gravity: 0.9,
  shapes: ["square", "circle", "triangle"] as const,
};

const CANVAS_ID = "celebration-confetti-canvas";

let cachedInstance: ReturnType<typeof confetti.create> | null = null;

// A single top-level canvas shared by every celebration so bursts are never
// clipped to a slide container and multiple bursts reuse one confetti worker.
function getConfettiInstance() {
  let canvas = document.getElementById(CANVAS_ID) as HTMLCanvasElement | null;
  if (!canvas) {
    canvas = document.createElement("canvas");
    canvas.id = CANVAS_ID;
    canvas.style.position = "fixed";
    canvas.style.inset = "0";
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.pointerEvents = "none";
    canvas.style.zIndex = "10000";
    document.body.appendChild(canvas);
  }
  if (!cachedInstance) {
    cachedInstance = confetti.create(canvas, {
      resize: true,
      useWorker: true,
    });
  }
  return cachedInstance;
}

// Creates the canvas and its worker ahead of the first celebration. Done lazily,
// the first burst had to lay out and paint a new canvas on the main thread just
// as the bite-completion screenshot was about to freeze it.
export function preloadCelebrationConfetti() {
  getConfettiInstance();
}

// Bite completion — rainbow confetti bursting upward out of the "Next bite
// coming up" toast. Reads the toast stack's on-screen position at fire-time and
// pops from its top-center; falls back to the bottom bar if the toast isn't up.
export function fireBoxBurst() {
  const toast = document.getElementById("notification-stack-next-slide");
  const rect = toast?.getBoundingClientRect();

  let originX = 0.5;
  let originY: number;
  if (rect && rect.width > 0) {
    originX = (rect.left + rect.width / 2) / window.innerWidth;
    originY = Math.max(0, (rect.top - 8) / window.innerHeight);
  } else {
    const bar = document.getElementById("tutor-bottom-bar");
    const barTop = bar?.getBoundingClientRect().top ?? window.innerHeight;
    originY = Math.max(0, (barTop - 8) / window.innerHeight);
  }

  const instance = getConfettiInstance();
  const base = {
    ...CONFETTI_DEFAULTS,
    origin: { x: originX, y: originY },
    scalar: 1.1,
    ticks: 200,
  };
  // Wide main burst plus two angled side jets for a fuller pop.
  instance({
    ...base,
    particleCount: 220,
    spread: 130,
    startVelocity: 45,
    angle: 90,
  });
  instance({
    ...base,
    particleCount: 70,
    spread: 60,
    startVelocity: 55,
    angle: 70,
  });
  instance({
    ...base,
    particleCount: 70,
    spread: 60,
    startVelocity: 55,
    angle: 110,
  });
}

// One frame of the lesson-complete effect: two cannons pinned to the very
// left/right viewport edges on the horizontal centerline (y = 0.5), each
// firing upward at 45° toward the center.
function fireEdgeCannons() {
  const instance = getConfettiInstance();
  const base = {
    ...CONFETTI_DEFAULTS,
    particleCount: 6,
    spread: 55,
    startVelocity: 60,
    scalar: 1.2,
    ticks: 260,
  };
  // Left edge, firing up and to the right (45°).
  instance({ ...base, angle: 45, origin: { x: -0.02, y: 0.5 } });
  // Right edge, firing up and to the left (135°).
  instance({ ...base, angle: 135, origin: { x: 1.02, y: 0.5 } });
}

// Lesson complete — continuous edge confetti. Streams from both edges every
// frame until the returned stop() is called. Pass a maxDurationMs safety cap.
export function fireRainbowConfetti(maxDurationMs = 60000): () => void {
  const start = Date.now();
  let stopped = false;

  const tick = () => {
    if (stopped || Date.now() - start > maxDurationMs) return;
    fireEdgeCannons();
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);

  return () => {
    stopped = true;
  };
}
